// =====================================================
// DASHBOARD STATS — Flux d'entrées (RUN 2 · RETOUCHE 1)
// =====================================================
// Cartes « Entrées de la semaine / du mois » (BPI entrés + PI convertis, avec Δ
// N-1) et histogramme « ID entrées » (barres N vs N-1). Fonctions pures,
// testables en vitest, sans dépendance React. Réutilise `buildDelta` (framework
// N-1) et `inWindow`.
//
// RETOUCHE 1 — pilotage par PLAGE DE DATES :
//   - Les deux cartes momentum (semaine/mois) sont ANCRÉES à la fin de la plage
//     sélectionnée (`window.end`) : « semaine » = 7 derniers jours avant la fin,
//     « mois » = 30 derniers jours avant la fin. N-1 = la même sous-fenêtre
//     décalée d'un an.
//   - L'histogramme couvre TOUTE la plage sélectionnée [début, fin], bucketé par
//     semaine ou par jour, chaque barre N-1 = le même bucket décalé d'un an sur
//     l'édition N-1. → montre l'évolution sur la plage choisie.
//
// Nomenclature (cf. nomenclature.ts, source de vérité RÉELLE desertours-dev) :
//   - BPI entrés   = soumissions aux formulaires generates_bpi (payées OU non),
//                    ENTRÉES (created_at) dans la fenêtre (réunion 22/07 : tous
//                    les BPI générés, pas seulement les payés).
//   - PI convertis = form_submissions (generates_bpi) dont converted_at tombe
//                    dans la fenêtre (une pré-inscription qui bascule en PI).
//   - ID entrées   = crews ayant atteint l'inscription définitive (isIdCrew),
//                    entrés (created_at) dans le bucket.

import { buildDelta } from './compute';
import { isIdCrew } from './nomenclature';
import { inWindow, shiftWindowYears } from './periods';
import type { DateWindow, DeltaValue, EditionRef, RawRows } from './types';

const DAY = 86_400_000;

// ---------------------------------------------------------------------------
// Compteurs windowés (édition + fenêtre)
// ---------------------------------------------------------------------------

/**
 * TOUS les BPI générés ENTRÉS (created_at) dans la fenêtre pour une édition
 * (réunion 22/07). Source = soumissions aux formulaires `generates_bpi` (payées
 * OU non) → aligné sur countBpi (carte KPI) : l'histogramme mensuel reflète
 * toutes les préinscriptions, pas seulement les paiements payés.
 */
export function countBpiEntres(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.bpiFormSubmissions.filter(
    (f) => f.product_id === editionId && inWindow(f.created_at, w)
  ).length;
}

/** Pré-inscriptions CONVERTIES (converted_at dans la fenêtre) pour une édition. */
export function countPiConvertis(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.bpiFormSubmissions.filter(
    (f) => f.product_id === editionId && inWindow(f.converted_at, w)
  ).length;
}

/** ID entrées (created_at dans la fenêtre) pour une édition. */
export function countIdEntres(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.crews.filter(
    (c) => c.product_id === editionId && isIdCrew(c) && inWindow(c.created_at, w)
  ).length;
}

// ---------------------------------------------------------------------------
// Cartes « Entrées de la semaine / du mois »
// ---------------------------------------------------------------------------

export type FluxWindowKey = 'week' | 'month';

/** Une carte flux : deux métriques (BPI entrés, PI convertis) avec Δ N-1. */
export interface FluxCardData {
  window: FluxWindowKey;
  bpiEntres: DeltaValue;
  piConvertis: DeltaValue;
}

export interface FluxParams {
  rows: RawRows;
  edition: EditionRef;
  /** Plage de dates sélectionnée (l'ancrage momentum = sa borne de fin). */
  window: DateWindow;
}

/** Sous-fenêtre momentum : `days` derniers jours se terminant à `end`. */
function trailingWindow(end: Date, days: number): DateWindow {
  return { start: new Date(end.getTime() - days * DAY), end };
}

/**
 * Construit une carte flux pour une sous-fenêtre momentum (7 ou 30 derniers jours
 * avant la fin de la plage). N-1 = la MÊME sous-fenêtre décalée d'un an sur
 * l'édition N-1 appariée (RETOUCHE 1). Absence de N-1 → Δ = N/A.
 */
export function computeFluxCard(
  { rows, edition, window }: FluxParams,
  key: FluxWindowKey
): FluxCardData {
  const days = key === 'week' ? 7 : 30;
  const w = trailingWindow(window.end, days);
  const wPrev = shiftWindowYears(w, -1);
  const prevId = edition.prevEditionId;

  const bpiN = countBpiEntres(rows, edition.id, w);
  const bpiPrev = prevId ? countBpiEntres(rows, prevId, wPrev) : null;

  const piN = countPiConvertis(rows, edition.id, w);
  const piPrev = prevId ? countPiConvertis(rows, prevId, wPrev) : null;

  return {
    window: key,
    bpiEntres: buildDelta(bpiN, bpiPrev),
    piConvertis: buildDelta(piN, piPrev),
  };
}

/** Les deux cartes flux (semaine + mois). */
export function computeFluxCards(params: FluxParams): { week: FluxCardData; month: FluxCardData } {
  return {
    week: computeFluxCard(params, 'week'),
    month: computeFluxCard(params, 'month'),
  };
}

// ---------------------------------------------------------------------------
// Histogramme « ID entrées » (barres N vs N-1) sur la plage sélectionnée
// ---------------------------------------------------------------------------

export type HistoGranularity = 'week' | 'day';

/** Un bucket de l'histogramme. `n1` null quand aucune édition N-1 appariée. */
export interface HistoBucket {
  /** Libellé FR court affiché sous la barre (ex. « 23 juin »). */
  label: string;
  /** Borne basse ISO du bucket (debug / tri). */
  start: string;
  /** ID entrées de l'édition N dans ce bucket. */
  n: number;
  /** ID entrées de l'édition N-1 dans le bucket équivalent année-1 (null si pas de N-1). */
  n1: number | null;
}

/** Minuit local du jour de `d` (copie, ne mute pas). */
function startOfDay(d: Date): Date {
  const c = new Date(d.getTime());
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Lundi 00:00 (semaine ISO) contenant `d`. */
function startOfIsoWeek(d: Date): Date {
  const c = startOfDay(d);
  const dow = (c.getDay() + 6) % 7; // 0 = lundi … 6 = dimanche
  return new Date(c.getTime() - dow * DAY);
}

const MONTHS_FR = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

function dayLabel(d: Date): string {
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}

/**
 * Découpe la plage [window.start, window.end] en buckets consécutifs (semaine ou
 * jour), du plus ancien au plus récent. Le 1er bucket est aligné (lundi / minuit)
 * sur, ou avant, le début de la plage ; chaque bucket est borné à la fin de plage.
 * Cap de sécurité : si le nombre de buckets quotidiens dépasse `maxBuckets`, on
 * bascule automatiquement en hebdomadaire (l'UI reste lisible).
 */
function bucketWindows(
  w: DateWindow,
  granularity: HistoGranularity,
  maxBuckets = 60
): DateWindow[] {
  const span = granularity === 'week' ? 7 * DAY : DAY;
  let cursor = granularity === 'week' ? startOfIsoWeek(w.start) : startOfDay(w.start);
  const out: DateWindow[] = [];
  while (cursor.getTime() <= w.end.getTime() && out.length <= maxBuckets * 2) {
    const bucketEnd = new Date(Math.min(cursor.getTime() + span - 1, w.end.getTime()));
    out.push({ start: new Date(cursor.getTime()), end: bucketEnd });
    cursor = new Date(cursor.getTime() + span);
  }
  return out;
}

export interface HistoParams extends FluxParams {
  granularity: HistoGranularity;
}

/**
 * Histogramme « ID entrées » couvrant TOUTE la plage sélectionnée, barres N vs
 * N-1. N-1 = le même bucket calendaire décalé d'un an, compté sur l'édition N-1
 * appariée. Pas de N-1 apparié → `n1` = null (l'UI n'affiche que N).
 */
export function computeIdHistogram({
  rows,
  edition,
  window,
  granularity,
}: HistoParams): HistoBucket[] {
  // Cap : quotidien sur une plage > 60 jours → bascule hebdo (évite 300 barres).
  const spanDays = Math.round((window.end.getTime() - window.start.getTime()) / DAY);
  const effective: HistoGranularity = granularity === 'day' && spanDays > 60 ? 'week' : granularity;

  const buckets = bucketWindows(window, effective);
  const prevId = edition.prevEditionId;

  return buckets.map((b) => {
    const bPrev = shiftWindowYears(b, -1);
    return {
      label: dayLabel(b.start),
      start: b.start.toISOString(),
      n: countIdEntres(rows, edition.id, b),
      n1: prevId ? countIdEntres(rows, prevId, bPrev) : null,
    };
  });
}
